/*
Copyright 2025 TekNix Corporation

This software is proprietary and confidential to TekNix Corporation.
All rights reserved. No part of this software may be reproduced, distributed,
or transmitted in any form or by any means, including photocopying, recording,
or other electronic or mechanical methods, without the prior written permission
of TekNix Corporation, except in the case of brief quotations embodied in
critical reviews and certain other noncommercial uses permitted by copyright law.

For permission requests, write to TekNix Corporation at the address below:
TekNix Corporation
Legal Department

UNAUTHORIZED REPRODUCTION OR DISTRIBUTION IS STRICTLY PROHIBITED.
*/

import type { BeaconEvent, BeaconEventHandlerMap } from '../models/beacon/beacon';
import type { XHubChatEvent } from '../models/event/event';
import type { EventTimelineSet } from '../models/event/event-timeline-set';
import type { Room } from '../models/room/room';
import type { RoomState } from '../models/room/room-state';
import type { IRoomSummary } from '../models/room/room-summary';
import type { Thread, ThreadEvent, ThreadEventHandlerMap as ThreadHandlerMap } from '../models/thread/thread';
import type { XHubChatEventHandlerMap } from './emitMapper';
import type { XHubChatEventEvent } from './event';
import type { EventTimelineSetHandlerMap } from './event-timeline-set';
import type { Membership } from './membership';
import type { RoomMemberEvent } from './room-member';
import type { RoomStateEvent, RoomStateEventHandlerMap } from './room-state';
import { EventStatus } from './event';

/**
 * Categories of rooms.
 */
export enum RoomCategory {
  Post = 'post',
  ChatRoom = 'chat_room',
}

/**
 * Events emitted by the Room class.
 */
export enum RoomEvent {
  MyMembership = 'Room.myMembership',
  Tags = 'Room.tags',
  AccountData = 'Room.accountData',
  Receipt = 'Room.receipt',
  Name = 'Room.name',
  Redaction = 'Room.redaction',
  LocalEchoUpdated = 'Room.localEchoUpdated',
  Timeline = 'Room.timeline',
  TimelineReset = 'Room.timelineReset',
  TimelineRefresh = 'Room.TimelineRefresh',
  OldStateUpdated = 'Room.OldStateUpdated',
  CurrentStateUpdated = 'Room.CurrentStateUpdated',
  UnreadNotifications = 'Room.UnreadNotifications',
  Summary = 'Room.Summary',
}

export type RoomEvents
  = | RoomEvent.Name
    | RoomEvent.Redaction
    | RoomEvent.Receipt
    | RoomEvent.Tags
    | RoomEvent.LocalEchoUpdated
    | RoomEvent.AccountData
    | RoomEvent.MyMembership
    | RoomEvent.Timeline
    | RoomEvent.TimelineReset;

export type RoomStateEvents
  = | RoomStateEvent.Events
    | RoomStateEvent.Members
    | RoomStateEvent.NewMember
    | RoomStateEvent.Update
    | RoomStateEvent.Marker;

export type RoomMemberEvents
  = | RoomMemberEvent.Typing
    | RoomMemberEvent.Membership;

export enum NotificationCountType {
  Highlight = 'highlight',
  Total = 'total',
}

export type NotificationCount = Partial<Record<NotificationCountType, number>>;

export type RoomEventHandlerMap = {
  /**
   * Fires when the logged in user's membership in the room is updated.
   *
   * @param room - The room in which the membership has been updated
   * @param membership - The new membership value
   * @param prevMembership - The previous membership value
   */
  [RoomEvent.MyMembership]: (room: Room, membership: Membership, prevMembership?: Membership) => void;
  /**
   * Fires whenever a room's tags are updated.
   * @param event - The tags event
   * @param room - The room whose Room.tags was updated.
   * @example
   * ```
   * XHubChatClient.on("Room.tags", function(event, room){
   *   var newTags = event.getContent().tags;
   *   if (newTags["favorite"]) showStar(room);
   * });
   * ```
   */
  [RoomEvent.Tags]: (event: XHubChatEvent, room: Room) => void;
  /**
   * Fires whenever a room's account_data is updated.
   * @param event - The account_data event
   * @param room - The room whose account_data was updated.
   * @param prevEvent - The event being replaced by
   * the new account data, if known.
   * @example
   * ```
   * XHubChatClient.on("Room.accountData", function(event, room, oldEvent){
   *   if (event.getType() === "m.room.colorscheme") {
   *       applyColorScheme(event.getContents());
   *   }
   * });
   * ```
   */
  [RoomEvent.AccountData]: (event: XHubChatEvent, room: Room, prevEvent?: XHubChatEvent) => void;
  /**
   * Fires whenever a receipt is received for a room
   * @param event - The receipt event
   * @param room - The room whose receipts was updated.
   * @example
   * ```
   * XHubChatClient.on("Room.receipt", function(event, room){
   *   var receiptContent = event.getContent();
   * });
   * ```
   */
  [RoomEvent.Receipt]: (event: XHubChatEvent, room: Room) => void;
  /**
   * Fires when an event we had previously received is redacted.
   *
   * (Note this is *not* fired when the redaction happens before we receive the
   * event).
   *
   * @param event - The matrix redaction event
   * @param room - The room containing the redacted event
   * @param threadId - The thread containing the redacted event (before it was redacted)
   */
  [RoomEvent.Redaction]: (event: XHubChatEvent, room: Room, threadId?: string) => void;
  /**
   * Fires whenever the name of a room is updated.
   * @param room - The room whose Room.name was updated.
   * @example
   * ```
   * XHubChatClient.on("Room.name", function(room){
   *   var newName = room.name;
   * });
   * ```
   */
  [RoomEvent.Name]: (room: Room) => void;
  /**
   * Fires when the status of a transmitted event is updated.
   *
   * <p>When an event is first transmitted, a temporary copy of the event is
   * inserted into the timeline, with a temporary event id, and a status of
   * 'SENDING'.
   *
   * <p>Once the echo comes back from the server, the content of the event
   * (XHubChatEvent.event) is replaced by the complete event from the server,
   * thus updating its event id, as well as server-generated fields such as the
   * timestamp. Its status is set to null.
   *
   * <p>Once the /send request completes, if the remote echo has not already
   * arrived, the event is updated with a new event id and the status is set to
   * 'SENT'. The server-generated fields are of course not updated yet.
   *
   * <p>If the /send fails, In this case, the event's status is set to
   * 'NOT_SENT'. If it is later resent, the process starts again, setting the
   * status to 'SENDING'. Alternatively, the message may be cancelled, which
   * removes the event from the room, and sets the status to 'CANCELLED'.
   *
   * <p>This event is raised to reflect each of the transitions above.
   *
   * @param event - The matrix event which has been updated
   *
   * @param room - The room containing the redacted event
   *
   * @param oldEventId - The previous event id (the temporary event id,
   *    except when updating a successfully-sent event when its echo arrives)
   *
   * @param oldStatus - The previous event status.
   */
  [RoomEvent.LocalEchoUpdated]: (event: XHubChatEvent, room: Room, oldEventId?: string, oldStatus?: EventStatus | null) => void;
  [RoomEvent.OldStateUpdated]: (room: Room, previousRoomState: RoomState, roomState: RoomState) => void;
  [RoomEvent.CurrentStateUpdated]: (room: Room, previousRoomState: RoomState, roomState: RoomState) => void;
  [RoomEvent.UnreadNotifications]: (unreadNotifications?: NotificationCount, threadId?: string) => void;
  [RoomEvent.TimelineRefresh]: (room: Room, eventTimelineSet: EventTimelineSet) => void;
  /**
   * Fires when a new room summary is returned by `/sync`.
   *
   * See https://spec.matrix.org/v1.8/client-server-api/#_XHubChatClientv3sync_roomsummary
   * for full details
   * @param summary - the room summary object
   */
  [RoomEvent.Summary]: (summary: IRoomSummary) => void;
  /**
   * Fires when a new thread is created in the room
   * @param thread - The newly created thread
   * @param toStartOfTimeline - Whether the thread was created from a root event at the start of the timeline
   * @returns void
   */
  [ThreadEvent.New]: (thread: Thread, toStartOfTimeline: boolean) => void;
} & Pick<ThreadHandlerMap, ThreadEvent.Update | ThreadEvent.NewReply | ThreadEvent.Delete>
& EventTimelineSetHandlerMap
& Pick<XHubChatEventHandlerMap, XHubChatEventEvent.BeforeRedaction> & Pick<
  RoomStateEventHandlerMap,
  | RoomStateEvent.Events
  | RoomStateEvent.Members
  | RoomStateEvent.NewMember
  | RoomStateEvent.Update
  | RoomStateEvent.Marker
  | BeaconEvent.New
> & Pick<BeaconEventHandlerMap, BeaconEvent.Update | BeaconEvent.Destroy | BeaconEvent.LivenessChange>;

// a map from current event status to a list of allowed next statuses
export const ALLOWED_TRANSITIONS: Record<EventStatus, EventStatus[]> = {
  [EventStatus.SENDING]: [EventStatus.QUEUED, EventStatus.NOT_SENT, EventStatus.SENT],
  [EventStatus.QUEUED]: [EventStatus.SENDING, EventStatus.NOT_SENT, EventStatus.CANCELLED],
  [EventStatus.SENT]: [],
  [EventStatus.NOT_SENT]: [EventStatus.SENDING, EventStatus.QUEUED, EventStatus.CANCELLED],
  [EventStatus.CANCELLED]: [],
};
